import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { IAppointment } from 'shared/interfaces/appointment.interface';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

import { outcomeOptions } from '../../constants/outcome-options';
import { specialtyOptions } from '../../constants/specialty-options';

@Component({
	selector: 'promptuary-outcome',
	templateUrl: './promptuary-outcome.component.html',
	styleUrls: ['./promptuary-outcome.component.scss'],
})
export class PromptuaryOutcomeComponent implements OnInit {
	constructor(
		public promptuaryService: PromptuaryService,
		public translateService: TranslateService,
		private actionTrackerService: ActionTrackerService
	) {}
	@Input() formGroup: FormGroup;
	@Input() appointment: IAppointment;
	@Input() autoSaveData;
	@Output() onChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() onSaving: EventEmitter<any> = new EventEmitter<any>();
	@Output() onSaved: EventEmitter<any> = new EventEmitter<any>();

	outcomeOptions = [];
	outcomeForwardJourneyOptions = [];
	numberOfJourneySessionsOptions = [];
	companyName = null;
	coordinatedCareConfiguration = null;
	selectedOnFowardJourneyTags = [];
	specialtyOptions = specialtyOptions;

	ngOnInit(): void {
		this.setOutcomeOptions();
		this.setDefaultOutcomeValue();
		this.setSpecialtyDefaultValue();
		this.getOutcomeForwardJourneyConfiguration(
			this.formGroup.get('outcome').value
		);
	}

	setDefaultOutcomeValue() {
		const value = this.formGroup.get('outcome').value;
		if (!value && !this.isTriageCoordinatedCareAppointment()) {
			this.formGroup.get('outcome').setValue('Em acompanhamento');
			this.onOutcomeChange({ value: 'Em acompanhamento' });
			return;
		}

		if (
			value &&
			!this.outcomeOptions.find((option) => option.value === value)
		) {
			this.formGroup.get('outcome').setValue(null);
		}
	}

	setOutcomeOptions() {
		this.outcomeOptions = this.isTriageCoordinatedCareAppointment()
			? ['Encaminhamento de plano de cuidados']
			: outcomeOptions.filter(
					(option) => option !== 'Encaminhamento de plano de cuidados'
			  );
		this.outcomeOptions = this.outcomeOptions.map((option) => ({
			label: option,
			value: option,
		}));
	}

	setSpecialtyDefaultValue() {
		const outcomeSpecialtyControl = this.formGroup.get('outcomeSpecialty');
		const specialtyValue = outcomeSpecialtyControl?.value;

		if (!specialtyValue) {
			outcomeSpecialtyControl?.setValue(null);
			return;
		}

		const isSpecialtyValid = this.specialtyOptions.some(
			(option) => option.value === specialtyValue
		);

		if (isSpecialtyValid) {
			this.onSpecialtyChange({ value: specialtyValue });
		} else {
			this.onSpecialtyChange({ value: 'others' });
			this.formGroup.get('outcomeOtherSpecialty')?.setValue(specialtyValue);
		}
	}

	onSpecialtyChange(option) {
		this.trackSelectSpecialty(option.value);
		const outcomeOtherSpecialtyControl = this.formGroup.get(
			'outcomeOtherSpecialty'
		);
		const outcomeOtherSpecialtyControlValue =
			outcomeOtherSpecialtyControl?.value;

		if (
			option.value !== 'others' &&
			outcomeOtherSpecialtyControlValue !== null
		) {
			this.clearOutputOtherSpecialty();
		}
		this.onChange.emit({ field: 'outcomeSpecialty', value: option.value });
		this.updateFormValidity();
	}

	clearOutputOtherSpecialty() {
		this.formGroup.get('outcomeOtherSpecialty')?.setValue(null);
		this.onChange.emit({ field: 'outcomeOtherSpecialty', value: null });
		localStorage.removeItem('outcomeOtherSpecialty');
	}

	trackSelectSpecialty(value) {
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Promptuary,
			SUBSCOPE_TYPE.Outcome,
			ITEM_TYPE.SpecialtyType,
			INTERACTION_TYPE.Select,
			{ promptuary_outcome_specialty: value }
		);
	}

	updateFormValidity() {
		this.formGroup.get('outcomeForwardJourney').updateValueAndValidity();
		this.formGroup
			.get('outcomeForwardJourneySessionNumber')
			.updateValueAndValidity();
		this.formGroup.get('outcomeSpecialty').updateValueAndValidity();
		this.formGroup.get('outcomeOtherSpecialty').updateValueAndValidity();
	}

	isTriageCoordinatedCareAppointment() {
		return this.appointment.subscription?.coordinatedCareType === 'screening';
	}

	getOutcomeForwardJourneyConfiguration(outcomeValue) {
		if (
			outcomeValue === 'Encaminhamento de plano de cuidados' &&
			this.outcomeForwardJourneyOptions.length === 0
		) {
			this.promptuaryService
				.getOutcomeForwardJourneyConfiguration(this.appointment.profileId)
				.toPromise()
				.then(({ companyName, coordinatedCareConfigurations }) => {
					this.companyName = companyName;
					this.coordinatedCareConfiguration = coordinatedCareConfigurations;
					this.outcomeForwardJourneyOptions = this.formatFowardOptions(
						coordinatedCareConfigurations
					);
					this.setNumberOfSessions();
				});
		}
	}

	onOutcomeChange($event) {
		const outcomeValue = $event.value;
		this.updateFormValidity();
		this.onChange.emit({ field: 'outcome', value: outcomeValue });
		this.onChange.emit({ field: 'outcomeSpecialty', value: null });
		this.onChange.emit({ field: 'outcomeOtherSpecialty', value: null });
		this.getOutcomeForwardJourneyConfiguration(outcomeValue);
	}

	onFowardJourneyChange() {
		this.formGroup.get('outcomeForwardJourneySessionNumber').setValue(null);
		this.setNumberOfSessions();
	}

	setNumberOfSessions() {
		const value = this.formGroup.get('outcomeForwardJourney').value;
		if (value === 'Não elegível para jornada') {
			return;
		}
		const selectedConfiguration = this.coordinatedCareConfiguration
			.find((config) => config.type === value)
			?.items.find((item) => item.type === 'therapy');
		const quantity = selectedConfiguration?.quantity;

		if (quantity) {
			const quantityArr = typeof quantity === 'number' ? [quantity] : quantity;
			this.numberOfJourneySessionsOptions = quantityArr.map((qtd) => ({
				label: qtd,
				value: qtd,
			}));
			const valueToSet =
				quantityArr.length > 1
					? this.formGroup.get('outcomeForwardJourneySessionNumber').value
					: quantityArr[0];
			this.setNumberOfSessionsValue(valueToSet);
		} else {
			this.numberOfJourneySessionsOptions = [];
			this.setNumberOfSessionsValue(0);
		}

		this.selectedOnFowardJourneyTags = this.outcomeForwardJourneyOptions.find(
			(option) =>
				option.value === this.formGroup.get('outcomeForwardJourney').value
		)?.tags;
	}

	setNumberOfSessionsValue(value) {
		this.formGroup.get('outcomeForwardJourneySessionNumber').setValue(value);
		this.onChange.emit({
			field: 'outcomeForwardJourneySessionNumber',
			value: value,
		});
	}

	formatFowardOptions(cordinatedCareConfigurations) {
		return cordinatedCareConfigurations.map((config) => {
			const journeyTranslateTitle = this.translateService.instant(
				`PROMPTUARY.JOURNEY_TYPES.${config.type}`
			);
			return {
				label: journeyTranslateTitle,
				value: config.type,
				tags: config.items
					.filter((item) => item.enabled)
					.map((item) => ({
						label: `${this.translateService.instant(
							`PROMPTUARY.JOURNEY_FEATURE_TYPES.${item.type.toUpperCase()}`
						)} ${
							typeof item.quantity === 'number' ? `(${item.quantity})` : ''
						}`,
						value: item.type,
					})),
			};
		});
	}
}
